import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './serviceCenter.module.css';

/**
 * 이용 절차 6단계.
 * 화면에는 3개씩 두 줄로 놓인다. 칸 크기가 글자 수에 따라 들쭉날쭉하지 않도록
 * 격자(grid)로 폭을 맞추고, 높이는 stretch 로 같이 맞춘다.
 */
const USE_STEPS = [
  { no: '01', main: '견적요청 · 전화상담' },
  { no: '02', main: '상담 후 견적서 제공', sub: '(전화, 메일, SMS 문자)' },
  { no: '03', main: '계약금 입금 · 계약서 작성' },
  { no: '04', main: '배차', sub: '(1일~3일 전)' },
  { no: '05', main: '배차 후 기사님 확인전화' },
  { no: '06', main: '당일 출발' },
];

/** 성수기 취소·환불 규정 */
const PEAK_RULES = [
  '운행예정일 ( ~ 10일 전까지) 취소 시 예약금액의 100% 환불',
  '운행예정일 (9일 전 ~ 7일 전까지) 취소 시 예약금액의 50% 환불',
  '운행예정일 (6일 전 ~ 4일 전까지) 취소 시 예약금액 환불 없음',
  '운행예정일 (3일 전부터 ~ 당일) 취소 시 예약금액 전액 환불불가 / 위약금 차량비 전체의 50%',
];

/** 비수기 취소·환불 규정 */
const OFF_PEAK_RULES = [
  '운행예정일 ( ~ 5일 전까지) 취소 시 예약금액의 100% 환불',
  '운행예정일 (4일 전 ~ 1일 전까지) 취소 시 예약금액의 50% 환불',
  '운행예정일 (당일) 취소 시 예약금 전액 환불불가 / 위약금 차량비 전체의 50%',
];

const ServiceCenter = () => {
  useEffect(() => {
    const titleElement = document.querySelector('title');
    titleElement.innerHTML = `이케이하나관광-고객센터`;
  }, []);

  // 하단의 «취소·환불규정» 링크(/center#refund)로 오면 그 칸으로 내려 준다.
  // 라우터는 # 위치로 스크롤하지 않는다.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0); // 하단 «고객센터» 링크로 와도 맨 위에서 시작
      return;
    }
    const target = document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <main className={styles.serviceCenter}>
      {/* 이용안내 */}
      <h1 className={styles.title}>이용안내</h1>
      <ol className={styles.useInfo}>
        {USE_STEPS.map((step) => (
          <li className={styles.useStep} key={step.no}>
            <span className={styles.useStepNumber}>{step.no}</span>
            <span className={styles.mainUseStep}>{step.main}</span>
            {step.sub && <span className={styles.subUseStep}>{step.sub}</span>}
          </li>
        ))}
      </ol>

      <ul className={styles.useInfoDetailContainer}>
        <li className={styles.useInfoDetail}>견적요청 시 자동으로 문자메시지가 발송됩니다.</li>
        <li className={styles.useInfoDetail}>
          모든 가격은 부가세 별도입니다.
        </li>
      </ul>

      {/* 취소 및 환불규정 */}
      <h1 className={styles.title} id='refund'>취소 및 환불규정</h1>
      <section className={styles.container}>
        <h2 className={styles.lead}>
          전세버스 대절의 취소에 따른 환불은 당사의 취소 및 환불규정에 따라 운임을 환불합니다.
        </h2>

        <p className={styles.paragraph}>
          당사의 환불규정은 적합한 상거래 및 상도덕에 준하며 전세버스 표준 약관에 준합니다.
          취소 및 환불에 대한 규정은 예약부도에 대한 당사의 손해를 방지하고, 장기적으로 고객님들에게
          보다 나은 서비스를 제공하는 차원에서 부과됩니다.
        </p>
        <p className={styles.paragraph}>
          특히 전세·관광버스의 특성상 운임요금이 성수기와 비수기의 수요·공급에 따라 다르게 책정되며,
          임차 운임 자체가 비싸고 미리 예약된 버스의 운행예약이 취소되면 다른 고객과의 계약이 불가능해지므로
          운휴 대체비용이 부가됩니다.
        </p>
        <p className={styles.paragraph}>
          단, 운수회사의 귀책으로 운행을 못하게 되었을 시 운수회사는 예약금 100%를 고객에게 지급해야 합니다.
        </p>
        <p className={styles.paragraph}>
          이 외에 민법의 규정에도 계약은 상호 신의성실의 원칙에 따라 지켜져야 한다고 규정되어 있으며,
          규정되지 않은 사항에 대해서는 상호 간의 합의에 따라 해지 및 환불을 해드리고 있습니다.
        </p>

        <h2 className={styles.lead}>차량 사용일 10일 전부터 취소에 대한 환불규정이 책정됩니다.</h2>
        <h2 className={styles.lead}>취소 및 환불규정은 비수기와 성수기로 구분됩니다.</h2>

        <div className={styles.seasonBox}>
          <h3 className={styles.date}>📅 성수기 : 4월~6월, 9월~11월</h3>
          <ul className={styles.detailContainer}>
            {PEAK_RULES.map((rule) => (
              <li className={styles.detail} key={rule}>{rule}</li>
            ))}
          </ul>
        </div>

        <div className={styles.seasonBox}>
          <h3 className={styles.date}>📅 비수기 : 성수기 이외의 달</h3>
          <ul className={styles.detailContainer}>
            {OFF_PEAK_RULES.map((rule) => (
              <li className={styles.detail} key={rule}>{rule}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* 고객센터 */}
      <h1 className={styles.title}>고객센터</h1>
      <section className={styles.container}>
        <h2 className={styles.lead}>
          (주)이케이하나관광은 24시간 상담이 가능하며 카톡 및 텔레그램으로도 실시간 상담이 가능합니다.
        </h2>

        <div className={styles.seasonBox}>
          <h3 className={styles.notice}>💻 고객센터 안내</h3>
          <ul className={styles.detailContainer}>
            <li className={styles.detail}>
              전화번호 : <a className={styles.telLink} href='tel:02-3432-6545'>02-3432-6545</a>
            </li>
            <li className={styles.detail}>팩스번호 : 02-6008-6545</li>
            <li className={styles.detail}>이메일 상담 : ektour0914@naver.com</li>
            <li className={styles.detail}>카톡아이디 : ektour</li>
            <li className={styles.detail}>텔레그램 아이디 : ektour</li>
            <li className={styles.detail}>
              버스대절 및 여행상담 : <a className={styles.telLink} href='tel:010-4968-6086'>010-4968-6086</a>
            </li>
          </ul>
        </div>

        <div className={styles.seasonBox}>
          <h3 className={styles.notice}>💸 계좌번호 안내</h3>
          <ul className={styles.detailContainer}>
            <li className={styles.detail}>예금주 : (주)이케이투어</li>
            <li className={styles.detail}>계좌번호 : 국민은행 810137-04-006627</li>
          </ul>
        </div>
      </section>
    </main>
  );
};

export default ServiceCenter;
